import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { InviteDto, MyInvitesDto } from '@/Domain/api/AI/dto/Invite.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

// Discriminates these events from the identical pair fired by AcceptInvitationPage.
const INVITATION_SOURCE = 'ai_preview_banner';

// Rendered inside AiPreview's fixed-height, overflow-hidden column, so every
// pixel here is taken from the preview below and can never be scrolled back:
// each row stays single-line in all 8 languages, and the component returns null
// when empty so the host slot's `empty:hidden` collapses its padding too.
export const AiInvitation = () => {
  const { t } = useTranslation(['accept_invitation', 'common']);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();
  const [busyToken, setBusyToken] = useState<string | null>(null);

  const { data } = useQuery<MyInvitesDto>({
    queryKey: [QueryKey.invites.mine],
    refetchOnMount: true,
  });

  const acceptMutation = useMutation<unknown, unknown, string>({
    mutationKey: [QueryKey.invites.accept],
  });

  const declineMutation = useMutation<unknown, unknown, string>({
    mutationKey: [QueryKey.invites.decline],
  });

  const invites = data?.invitations ?? [];

  const handleAccept = async (invite: InviteDto): Promise<void> => {
    if (busyToken !== null) return;
    setBusyToken(invite.token);
    try {
      await acceptMutation.mutateAsync(invite.token);
      capture(PostHogEventEnum.InvitationAccepted, {
        scenario_id: invite.scenario_id,
        role: invite.role,
        pending_invitation_count: invites.length,
        has_inviter_email: invite.inviter_email != null,
        source: INVITATION_SOURCE,
      });
      // Full reload so the JWT carries the new scenario role.
      window.location.assign(`/i/${invite.scenario_id}/`);
    } catch (error) {
      console.error('Error accepting invite:', error);
      toast.error(t('action_failed'));
      setBusyToken(null);
    }
  };

  const handleDecline = async (invite: InviteDto): Promise<void> => {
    if (busyToken !== null) return;
    setBusyToken(invite.token);
    try {
      await declineMutation.mutateAsync(invite.token);
      capture(PostHogEventEnum.InvitationDeclined, {
        scenario_id: invite.scenario_id,
        role: invite.role,
        remaining_invitation_count: invites.length - 1,
        source: INVITATION_SOURCE,
      });
      // Cancel first: a /my_invites refetch already in flight would otherwise land
      // after this write and put the declined row back.
      await queryClient.cancelQueries({ queryKey: [QueryKey.invites.mine] });
      queryClient.setQueryData<MyInvitesDto>(
        [QueryKey.invites.mine],
        (previous) =>
          previous && {
            ...previous,
            invitations: previous.invitations.filter((item) => item.token !== invite.token),
          },
      );
    } catch (error) {
      console.error('Error declining invite:', error);
      toast.error(t('action_failed'));
    } finally {
      setBusyToken(null);
    }
  };

  if (invites.length === 0) return null;

  return (
    <div className="flex flex-col gap-2" data-testid="ai-invitations">
      {invites.map((invite) => {
        const agent = invite.one_word_description?.trim() || t('untitled_agent');
        const role = t(`common:role.${invite.role}`, invite.role);
        const message = invite.inviter_email
          ? t('banner.message_with_inviter', { inviter: invite.inviter_email, agent, role })
          : t('banner.message', { agent, role });
        const messageId = `ai-invitation-message-${invite.token}`;

        return (
          <div
            key={invite.token}
            role="group"
            aria-labelledby={messageId}
            data-testid={`ai-invitation-${invite.token}`}
            className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5"
          >
            <span
              aria-hidden="true"
              className="grid size-8 shrink-0 place-items-center rounded-lg bg-info-muted text-info"
            >
              <UserPlus className="size-4" />
            </span>

            <p
              id={messageId}
              title={message}
              data-testid={messageId}
              className="min-w-0 flex-1 truncate text-sm text-foreground"
            >
              {message}
            </p>

            <div className="flex shrink-0 items-center gap-1.5">
              {/* Disabled per row, not globally: dimming every row reads as "all of them are being accepted". */}
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('banner.decline')}
                loading={busyToken === invite.token && declineMutation.isPending}
                disabled={busyToken === invite.token}
                onClick={() => void handleDecline(invite)}
                data-testid={`ai-invitation-decline-${invite.token}`}
              >
                <X className="sm:hidden" aria-hidden="true" />
                <span className="hidden sm:inline">{t('banner.decline')}</span>
              </Button>
              <Button
                variant="dark"
                size="sm"
                loading={busyToken === invite.token && acceptMutation.isPending}
                disabled={busyToken === invite.token}
                onClick={() => void handleAccept(invite)}
                data-testid={`ai-invitation-accept-${invite.token}`}
              >
                {t('banner.accept')}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
